import {
  Component, ElementRef, EventEmitter, Input, Output, ViewChild,
  OnChanges, SimpleChanges
} from '@angular/core';
import { HopRound } from '../../../interfaces/fraction-hops.interface';

interface Arc { d: string; index: number; }
interface Sparkle { x: number; y: number; r: number; delay: number; }

/**
 * Fraction Hops play surface — a 0–`maxTick` number line with a frog on
 * a launch pad. Each HOP leaps the frog forward by one unit fraction
 * (drawing an arc + bumping the "×" counter); LAND commits. Landing on
 * exactly `whole` hops (i.e. at the product) reveals the `N×` brace and
 * the product as a mixed number; a wrong count buzzes red and lets them
 * fix it with Undo/Hop.
 *
 * Presentation-only: HOP / UNDO / LAND are called by the shell buttons
 * via @ViewChild; the outcome is reported with `solved` / `missed`.
 */
@Component({
  selector: 'app-fraction-hops-stage',
  templateUrl: './fraction-hops-stage.component.html',
  styleUrls: ['./fraction-hops-stage.component.scss']
})
export class FractionHopsStageComponent implements OnChanges {
  @Input() round: HopRound | null = null;
  @Input() roundKey = 0;
  @Input() interactive = true;
  @Input() currLang: any = 'en';

  @Output() solved = new EventEmitter<void>();
  @Output() missed = new EventEmitter<void>();

  @ViewChild('svg', { static: true }) svgRef!: ElementRef<SVGSVGElement>;

  readonly VIEW_W = 1000;
  readonly VIEW_H = 520;
  readonly X0 = 110;
  readonly X_END = 890;
  readonly LINE_Y = 340;
  readonly ARC_H = 66;

  count = 0;                 // hops taken
  solvedState = false;
  wrongFlash = false;
  justHopped = false;
  sparkles: Sparkle[] = [];

  private timers: any[] = [];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['round'] || changes['roundKey']) this.reset();
  }

  private reset(): void {
    this.clearTimers();
    this.count = 0;
    this.solvedState = false;
    this.wrongFlash = false;
    this.justHopped = false;
    this.sparkles = [];
  }

  // ── Geometry ───────────────────────────────────────────────────────

  private get maxTick(): number { return this.round?.maxTick ?? 4; }
  private get unit(): number { return (this.X_END - this.X0) / this.maxTick; }
  valueToX(v: number): number { return this.X0 + v * this.unit; }

  get hop(): number { return this.round?.hop ?? 0; }
  get position(): number { return this.count * this.hop; }   // current value
  get frogX(): number { return this.valueToX(Math.min(this.position, this.maxTick)); }

  /** Cap hops so the frog can't leave the line. */
  private get maxHops(): number {
    if (!this.round || this.hop <= 0) return 0;
    return Math.floor(this.maxTick / this.hop);
  }

  get ticks(): number[] {
    return Array.from({ length: this.maxTick + 1 }, (_, i) => i);
  }

  /** One arc per completed hop. */
  get arcs(): Arc[] {
    const out: Arc[] = [];
    for (let i = 0; i < this.count; i++) {
      const x1 = this.valueToX(i * this.hop);
      const x2 = this.valueToX((i + 1) * this.hop);
      const mid = (x1 + x2) / 2;
      out.push({ index: i, d: `M ${x1} ${this.LINE_Y} Q ${mid} ${this.LINE_Y - this.ARC_H} ${x2} ${this.LINE_Y}` });
    }
    return out;
  }

  /** Brace span over all the hops (shown on success). */
  get braceD(): string {
    const x1 = this.valueToX(0);
    const x2 = this.valueToX(this.position);
    const top = this.LINE_Y - this.ARC_H - 26;
    const mid = (x1 + x2) / 2;
    return `M ${x1} ${this.LINE_Y - this.ARC_H - 6} L ${x1} ${top} L ${mid} ${top} `
      + `M ${mid} ${top} L ${x2} ${top} L ${x2} ${this.LINE_Y - this.ARC_H - 6}`;
  }
  get braceMidX(): number { return this.valueToX(this.position / 2); }
  get braceTopY(): number { return this.LINE_Y - this.ARC_H - 34; }

  /** Product as a mixed number for the reveal, e.g. "1 1/2" or "2".
   *  `improperNum` is the total count of unit fractions (N hops), i.e.
   *  the numerator of the improper fraction N/den. */
  get productParts(): { whole: number; num: number; den: number; improperNum: number } | null {
    if (!this.round) return null;
    const den = this.round.den;
    const totalNum = this.round.whole * this.round.num;   // over den
    const whole = Math.floor(totalNum / den);
    const num = totalNum % den;
    return { whole, num, den, improperNum: totalNum };
  }

  /** Landing x, clamped so the product pill stays inside the panel. */
  get productX(): number {
    return Math.max(130, Math.min(this.VIEW_W - 130, this.frogX));
  }

  /** Educational product label shown on the line, e.g. "7/4 = 1 3/4",
   *  "8/4 = 2", or "3/4" — connecting N hops (N/d) to its value. */
  get productLabel(): string {
    const p = this.productParts;
    if (!p) return '';
    const improper = `${p.improperNum}/${p.den}`;
    if (p.num === 0) return `${improper} = ${p.whole}`;   // whole number
    if (p.whole === 0) return improper;                   // value is < 1
    return `${improper} = ${p.whole} ${p.num}/${p.den}`;  // mixed number
  }

  get pillW(): number {
    return Math.max(130, this.productLabel.length * 23 + 44);
  }

  // ── Actions (called by the shell) ──────────────────────────────────

  get canHop(): boolean { return this.interactive && !this.solvedState && this.count < this.maxHops; }
  get canUndo(): boolean { return this.interactive && !this.solvedState && this.count > 0; }
  get canLand(): boolean { return this.interactive && !this.solvedState && this.count > 0; }

  hop_(): void {
    if (!this.canHop) return;
    this.wrongFlash = false;
    this.count++;
    this.bumpHop();
  }

  undo(): void {
    if (!this.canUndo) return;
    this.wrongFlash = false;
    this.count--;
  }

  land(): void {
    if (!this.canLand || !this.round) return;
    if (this.count === this.round.whole) {
      this.solvedState = true;
      this.spawnSparkles(this.frogX);
      this.solved.emit();
    } else {
      this.wrongFlash = true;
      this.timers.push(setTimeout(() => { this.wrongFlash = false; }, 620));
      this.missed.emit();
    }
  }

  private bumpHop(): void {
    this.justHopped = false;
    requestAnimationFrame(() => { this.justHopped = true; });
  }

  private spawnSparkles(cx: number): void {
    this.sparkles = Array.from({ length: 12 }, (_, i) => {
      const a = (i / 12) * Math.PI * 2;
      const dist = 28 + (i % 3) * 18;
      return { x: cx + Math.cos(a) * dist, y: this.LINE_Y - 20 + Math.sin(a) * dist, r: 4 + (i % 3) * 3, delay: (i % 5) * 35 };
    });
  }

  private clearTimers(): void {
    this.timers.forEach(t => clearTimeout(t));
    this.timers = [];
  }
}
